import { FormEvent, useCallback, useEffect, useState } from "react";
import type { TenantSlugSuggestion } from "@itaas/pep-contracts";
import { checkCommercialTenantSlug, suggestCommercialTenantSlugs } from "../../api/pep-api";
import { isTenantSlugFormatValid, normalizeTenantSlugInput } from "./trial-phone.utils";

interface TrialTenantSlugStepProps {
  company: string;
  city?: string;
  value: string;
  onChange: (slug: string) => void;
  onBack: () => void;
  onConfirm: () => void;
  submitting: boolean;
  error: string | null;
  confirmLabel: string;
}

export function TrialTenantSlugStep({
  company,
  city,
  value,
  onChange,
  onBack,
  onConfirm,
  submitting,
  error,
  confirmLabel,
}: TrialTenantSlugStepProps) {
  const [domain, setDomain] = useState("itaas-suite.com");
  const [suggestions, setSuggestions] = useState<TenantSlugSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [slugTouched, setSlugTouched] = useState(false);
  const [checkState, setCheckState] = useState<"idle" | "checking" | "ok" | "taken" | "invalid">(
    "idle",
  );

  const loadSuggestions = useCallback(async () => {
    if (!company.trim()) return;
    setLoadingSuggestions(true);
    try {
      const result = await suggestCommercialTenantSlugs(company.trim(), city?.trim());
      setDomain(result.domain);
      setSuggestions(result.suggestions);
      if (!slugTouched) {
        const firstAvailable = result.suggestions.find((s) => s.available);
        if (firstAvailable) {
          onChange(firstAvailable.slug);
        }
      }
    } catch {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [company, city, slugTouched, onChange]);

  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    const slug = value.trim();
    if (!slug) {
      setCheckState("idle");
      return;
    }
    if (!isTenantSlugFormatValid(slug)) {
      setCheckState("invalid");
      return;
    }
    setCheckState("checking");
    const timer = window.setTimeout(() => {
      void checkCommercialTenantSlug(slug)
        .then((r) => {
          if (!r.valid) setCheckState("invalid");
          else if (r.available) setCheckState("ok");
          else setCheckState("taken");
        })
        .catch(() => setCheckState("idle"));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [value]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (checkState !== "ok") return;
    onConfirm();
  }

  const previewEmail = value.trim()
    ? `admin@${value.trim().toLowerCase()}.${domain}`
    : `admin@votre-espace.${domain}`;

  return (
    <form className="card border-0 shadow-sm p-4" onSubmit={handleSubmit}>
      <h2 className="h6 fw-bold mb-2">Choisissez l&apos;identifiant de votre espace</h2>
      <p className="text-secondary small mb-4">
        C&apos;est l&apos;adresse courte de votre organisation sur ITaaS — comme un nom d&apos;utilisateur
        Google. Privilégiez quelque chose de court et facile à retenir.
      </p>

      <div className="mb-3">
        <label className="form-label" htmlFor="tenant-slug">
          Identifiant espace *
        </label>
        <div className="input-group">
          <input
            id="tenant-slug"
            className="form-control font-monospace"
            value={value}
            onChange={(e) => {
              setSlugTouched(true);
              onChange(normalizeTenantSlugInput(e.target.value));
            }}
            placeholder="cslc427"
            required
            minLength={3}
            maxLength={24}
            pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
            autoComplete="off"
          />
          <span className="input-group-text text-muted">.{domain}</span>
        </div>
        {checkState === "checking" && (
          <div className="form-text">Vérification de la disponibilité…</div>
        )}
        {checkState === "ok" && (
          <div className="form-text text-success">✓ Disponible</div>
        )}
        {checkState === "taken" && (
          <div className="form-text text-danger">Cet identifiant est déjà pris — choisissez une suggestion.</div>
        )}
        {checkState === "invalid" && value.trim() && (
          <div className="form-text text-danger">
            3 à 24 caractères : lettres minuscules, chiffres et tirets (sans tiret au début ou à la fin).
          </div>
        )}
      </div>

      <p className="small mb-3">
        Connexion admin : <code>{previewEmail}</code>
      </p>

      <div className="mb-4">
        <p className="small fw-semibold mb-2">Suggestions</p>
        {loadingSuggestions ? (
          <p className="small text-secondary mb-0">Génération des suggestions…</p>
        ) : suggestions.length === 0 ? (
          <p className="small text-secondary mb-0">Saisissez un identifiant personnalisé ci-dessus.</p>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s.slug}
                type="button"
                className={`btn btn-sm ${
                  value === s.slug
                    ? "btn-primary"
                    : s.available
                      ? "btn-outline-primary"
                      : "btn-outline-secondary"
                }`}
                disabled={!s.available}
                title={s.available ? s.label : "Déjà utilisé"}
                onClick={() => {
                  setSlugTouched(true);
                  onChange(s.slug);
                }}
              >
                <span className="font-monospace">{s.slug}</span>
                {!s.available && (
                  <span className="ms-1 opacity-75" aria-hidden>
                    ✕
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <p className="form-text mt-2 mb-0">
          Sigles, mots clés et codes numériques courts — évitez les noms complets trop longs.
        </p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="d-flex flex-column flex-sm-row gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          ← Organisation
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-lg flex-grow-1"
          disabled={submitting || checkState !== "ok"}
        >
          {submitting ? "Activation…" : confirmLabel}
        </button>
      </div>
    </form>
  );
}
